import { Button } from '@up2stay/up2promo-ui';
import { Card, CardContent, CardHeader, CardTitle } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { CopyButton } from '@up2stay/up2promo-ui';
import {
  ghostAccentIconButtonClass,
  nestedSecondarySurfaceClass,
  qrWellSurfaceClass,
  sectionAccentIconClass,
} from '@up2stay/up2promo-ui';
import { QRCode } from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import {
  CheckCircle2,
  ExternalLink,
  Mail,
  MessageSquare,
  RefreshCw,
  Share2,
  Wallet,
} from 'lucide-react';

import { useIsMobile } from '@/core/hooks/use-mobile';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';

export type ProfilesSharePanelProps = {
  dict: {
    profiles: {
      share: Record<string, unknown> & {
        title: string;
        description: string;
        qrCode: string;
        profileLink: string;
        loading: string;
        shareViaSms: string;
        shareViaEmail: string;
        viewProfile: string;
      };
    };
  };
  isLoading: boolean;
  profile: { id?: string } | null | undefined;
  profileLink: string | null;
  directProfileUrl: string | null;
  userUsername: string | null | undefined;
  walletEnabled: boolean;
  walletCardState: string | null | undefined;
  walletButton: { label: string; hint: string | null };
  isAddingToWallet: boolean;
  onShareCopied: () => void;
  onShareViaSms: () => void;
  onShareViaEmail: () => void;
  onAddToWallet: () => void;
};

export function ProfilesSharePanel({
  dict,
  isLoading,
  profile,
  profileLink,
  directProfileUrl,
  userUsername,
  walletEnabled,
  walletCardState,
  walletButton,
  isAddingToWallet,
  onShareCopied,
  onShareViaSms,
  onShareViaEmail,
  onAddToWallet,
}: ProfilesSharePanelProps) {
  const isMobile = useIsMobile();
  const share = dict.profiles.share;
  const markGetStartedShareDone = onShareCopied;

  const qrFrame = (
    <div
      className={cn(
        'flex flex-shrink-0 items-center justify-center',
        qrWellSurfaceClass,
        isMobile
          ? 'size-[158px] rounded-[18px] p-4'
          : 'size-32 rounded-[14px] p-3',
      )}
    >
      {isLoading && !profileLink ? (
        <Skeleton className="h-full w-full rounded-lg" />
      ) : profileLink ? (
        <QRCode
          data={profileLink}
          className="size-full [&_svg]:bg-transparent"
        />
      ) : (
        <div className="space-y-2 text-center">
          <div className="mx-auto h-6 w-6 rounded-sm bg-muted" />
          <div className="font-mono text-sm text-muted-foreground">
            {share.qrCode}
          </div>
        </div>
      )}
    </div>
  );

  const shareActions = (
    <div className={cn('grid grid-cols-2', isMobile ? 'gap-2.5' : 'gap-2')}>
      {isLoading && !profile ? (
        <>
          <Skeleton className="h-10 w-full rounded-[10px]" />
          <Skeleton className="h-10 w-full rounded-[10px]" />
          <Skeleton className="col-span-2 h-10 w-full rounded-[10px]" />
        </>
      ) : (
        <>
          <Button
            variant="outline"
            size="md"
            className={cn(
              'justify-center gap-1.5 text-[12.5px] font-semibold',
              isMobile ? 'min-h-11 rounded-xl' : 'rounded-[10px]',
            )}
            disabled={!directProfileUrl}
            onClick={onShareViaSms}
          >
            <MessageSquare className="size-[15px] text-muted-foreground" />
            {share.shareViaSms}
          </Button>
          <Button
            variant="outline"
            size="md"
            className={cn(
              'justify-center gap-1.5 text-[12.5px] font-semibold',
              isMobile ? 'min-h-11 rounded-xl' : 'rounded-[10px]',
            )}
            disabled={!directProfileUrl}
            onClick={onShareViaEmail}
          >
            <Mail className="size-[15px] text-muted-foreground" />
            {share.shareViaEmail}
          </Button>
          <Button
            variant="ghostAccent"
            size="md"
            className={cn(
              'col-span-2 justify-center gap-1.5 text-[12.5px] font-semibold',
              isMobile ? 'min-h-11 rounded-xl' : 'rounded-[10px]',
            )}
            disabled={!directProfileUrl}
            onClick={() => {
              if (directProfileUrl) {
                markGetStartedShareDone();
                window.open(directProfileUrl, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            <ExternalLink className="size-[15px]" />
            {share.viewProfile}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Card
      className="profiles-share-card flex flex-1 flex-col overflow-hidden min-w-0"
      data-guidance-target="share-profile"
    >
      <CardHeader className="gap-1.5 pb-0 max-md:p-5 md:p-6">
        <CardTitle className="flex items-center gap-2.5 text-[15.5px] font-bold leading-tight max-md:gap-2.5 max-md:text-[15px]">
          <Share2
            className={cn(sectionAccentIconClass, 'max-md:size-[18px]')}
          />
          {share.title}
        </CardTitle>
        <p className="text-[13px] text-muted-foreground max-md:text-[12.5px]">
          {share.description}
        </p>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-1 flex-col gap-4 overflow-hidden p-5 pt-4 md:p-6 md:pt-[18px]">
        <div
          className={cn(
            'flex min-w-0',
            isMobile
              ? 'flex-col items-stretch gap-4'
              : 'flex-row items-center gap-5',
          )}
        >
          {isMobile ? (
            <div className="flex justify-center">{qrFrame}</div>
          ) : (
            qrFrame
          )}

          <div className="min-w-0 flex-1">
            {!isMobile ? (
              <p className="mb-2 min-w-0 break-words text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                {share.profileLink}
              </p>
            ) : null}

            <div
              className={cn(
                'mb-3 flex min-w-0 items-center gap-2',
                nestedSecondarySurfaceClass,
                isMobile
                  ? 'rounded-xl py-1.5 pl-3.5 pr-1.5'
                  : 'mb-3 rounded-[10px] py-1 pl-3.5 pr-1',
              )}
            >
              {isLoading && !directProfileUrl ? (
                <>
                  <Skeleton className="h-5 flex-1" />
                  <Skeleton className="size-8 shrink-0 rounded-lg" />
                </>
              ) : (
                <>
                  <div
                    className={cn(
                      'min-w-0 flex-1 truncate text-[13px]',
                      directProfileUrl
                        ? 'text-foreground/80'
                        : 'text-muted-foreground italic',
                    )}
                  >
                    {directProfileUrl ||
                      (!userUsername
                        ? (share as { noUsernameHint?: string })
                            .noUsernameHint || ''
                        : share.loading)}
                  </div>
                  {directProfileUrl ? (
                    <CopyButton
                      content={directProfileUrl}
                      aria-label={
                        (share as { copyProfileLink?: string }).copyProfileLink
                      }
                      variant="ghost"
                      whileHover={undefined}
                      whileTap={undefined}
                      className={cn(
                        'shrink-0',
                        isMobile
                          ? 'size-[34px] rounded-[9px]'
                          : 'size-8 rounded-lg',
                        ghostAccentIconButtonClass,
                      )}
                      onCopy={() => {
                        void triggerTapHaptic();
                        markGetStartedShareDone();
                      }}
                    />
                  ) : null}
                </>
              )}
            </div>

            {!userUsername && profile ? (
              <p className="mb-3 text-sm text-muted-foreground">
                {(share as { usernameRequired?: string }).usernameRequired ||
                  ''}
              </p>
            ) : null}

            {shareActions}

            {walletEnabled ? (
              <div className="mt-3 space-y-2">
                {isLoading && !profile ? (
                  <Skeleton className="h-10 w-full rounded-[10px]" />
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="md"
                      className={cn(
                        'w-full justify-center gap-1.5 text-[12.5px] font-semibold',
                        isMobile ? 'min-h-11 rounded-xl' : 'rounded-[10px]',
                        walletCardState === 'outdated' &&
                          'border-amber-500/60 text-amber-700 dark:text-amber-400',
                      )}
                      onClick={onAddToWallet}
                      disabled={!profile || !profileLink || isAddingToWallet}
                    >
                      {walletCardState === 'outdated' ? (
                        <RefreshCw className="size-[15px]" />
                      ) : walletCardState === 'current' ? (
                        <CheckCircle2 className="size-[15px]" />
                      ) : (
                        <Wallet className="size-[15px]" />
                      )}
                      {walletButton.label}
                    </Button>
                    {walletButton.hint ? (
                      <p className="text-sm text-muted-foreground">
                        {walletButton.hint}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
